import Place from "../Models/Place";

// Session-scoped cache of Build My Trip's place search results, keyed by
// destination (city+country). Building a SECOND guide for a destination
// already searched earlier in the same browser session (e.g. a group trip
// where the user builds one guide per traveler, or trying a few variant
// itineraries) currently re-runs the same paid Google/AI search from
// scratch. sessionStorage survives page reloads (a plain in-memory variable
// wouldn't) but clears itself when the tab/window actually closes, so no
// staleness policy is needed the way a persisted cross-session cache would
// require. See project memory: project_session_place_search_cache_idea.
// Implemented 2026-09-27.
const STORAGE_PREFIX = "bmt_place_search_cache:";

const cacheKey = (city, country) => `${STORAGE_PREFIX}${(city || "").trim().toLowerCase()}|${(country || "").trim().toLowerCase()}`;

// Returns an array of real Place instances (reconstructed from the cached
// plain data — sessionStorage only round-trips JSON, not class instances/
// methods) for this destination, or null on a cache miss/any read failure.
export function getCachedSearchResults(city, country) {
  try {
    const raw = window.sessionStorage.getItem(cacheKey(city, country));
    // Temporary — visible in the browser console. Added 2026-09-28 to
    // confirm the cache is actually being hit (matches mobile's identical
    // [BMT cache] logs in this same file, checked via adb logcat). Remove
    // once confirmed. See project memory: project_session_place_search_cache_idea.
    console.log(`[BMT cache] read ${cacheKey(city, country)}: ${raw ? "HIT" : "MISS"}`);
    if (!raw) return null;
    const plainPlaces = JSON.parse(raw);
    if (!Array.isArray(plainPlaces) || plainPlaces.length === 0) return null;
    return plainPlaces.map((data) => {
      const place = new Place(null);
      Object.assign(place, data);
      return place;
    });
  } catch (e) {
    return null;
  }
}

// places: the raw search results (Place instances or plain objects) exactly
// as returned by the search, before scheduling/category-remapping —
// caching the raw search output (not what actually got added to a
// particular guide) keeps this reusable across different guides/schedules
// built for the same destination.
export function setCachedSearchResults(city, country, places) {
  try {
    if (!Array.isArray(places) || places.length === 0) return;
    window.sessionStorage.setItem(cacheKey(city, country), JSON.stringify(places));
    // Temporary — see getCachedSearchResults's identical note.
    console.log(`[BMT cache] write ${cacheKey(city, country)}: ${places.length} places`);
  } catch (e) {
    // sessionStorage can throw (quota exceeded, private browsing, etc.) —
    // caching is a pure optimization, never something a search should fail
    // over.
  }
}
